import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  BarChart3, 
  Calendar, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  TrendingUp,
  RefreshCw,
  Users
} from 'lucide-react';
import { toast } from "sonner";
import apiService from '@/services/api';

export default function WeeklyAttendanceSummary({ classId, className }) {
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [classWeeklyData, setClassWeeklyData] = useState(null);
  const [viewMode, setViewMode] = useState('overview'); // 'overview' or 'detailed'

  useEffect(() => {
    fetchWeeklySummary();
  }, []);

  useEffect(() => {
    if (classId && viewMode === 'detailed') {
      fetchClassWeeklyAttendance();
    }
  }, [classId, viewMode]);

  const fetchWeeklySummary = async () => {
    try {
      setLoading(true);
      const data = await apiService.getWeeklyAttendanceSummary();
      setSummaryData(data);
    } catch (error) {
      console.error('Error fetching weekly summary:', error);
      toast.error('Failed to load weekly summary');
    } finally {
      setLoading(false);
    }
  };

  const fetchClassWeeklyAttendance = async () => {
    if (!classId) return;
    try {
      const data = await apiService.getClassWeeklyAttendance(classId);
      setClassWeeklyData(data);
    } catch (error) {
      console.error('Error fetching class weekly attendance:', error);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'present': return 'bg-green-500';
      case 'late': return 'bg-amber-500';
      case 'absent': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'present': return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'late': return <Clock className="h-4 w-4 text-amber-600" />;
      case 'absent': return <XCircle className="h-4 w-4 text-red-600" />;
      default: return <span className="h-4 w-4 inline-block bg-gray-200 rounded-full" />;
    }
  };

  const getDayAbbrev = (day) => {
    return day.substring(0, 3);
  };

  if (loading) {
    return (
      <Card className="border-purple-200">
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-2">
            <RefreshCw className="h-5 w-5 animate-spin text-purple-600" />
            <span>Loading weekly summary...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!summaryData) {
    return (
      <Card className="border-purple-200">
        <CardContent className="py-8 text-center text-muted-foreground">
          <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No attendance data available for this week</p>
        </CardContent>
      </Card>
    );
  }

  const { daily_summary, week_totals, week_start, week_end } = summaryData;
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  return (
    <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-purple-600" />
              Weekly Attendance Summary
            </CardTitle>
            <CardDescription className="flex items-center gap-1 mt-1">
              <Calendar className="h-3 w-3" />
              {new Date(week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(week_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant={viewMode === 'overview' ? 'default' : 'outline'}
              onClick={() => setViewMode('overview')}
              className={viewMode === 'overview' ? 'bg-purple-600 hover:bg-purple-700' : ''}
            >
              Overview
            </Button>
            {classId && (
              <Button 
                size="sm" 
                variant={viewMode === 'detailed' ? 'default' : 'outline'}
                onClick={() => setViewMode('detailed')}
                className={viewMode === 'detailed' ? 'bg-purple-600 hover:bg-purple-700' : ''}
              >
                {className || 'Class Details'}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {viewMode === 'overview' ? (
          <>
            {/* Week Totals Summary */}
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-white rounded-lg p-3 border border-purple-100 text-center">
                <div className="text-2xl font-bold text-purple-600">{week_totals.total}</div>
                <div className="text-xs text-muted-foreground">Total Records</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-green-100 text-center">
                <div className="text-2xl font-bold text-green-600">{week_totals.present}</div>
                <div className="text-xs text-muted-foreground">Present</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-amber-100 text-center">
                <div className="text-2xl font-bold text-amber-600">{week_totals.late}</div>
                <div className="text-xs text-muted-foreground">Late</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-red-100 text-center">
                <div className="text-2xl font-bold text-red-600">{week_totals.absent}</div>
                <div className="text-xs text-muted-foreground">Absent</div>
              </div>
            </div>

            {/* Weekly Attendance Rate */}
            <div className="bg-white rounded-lg p-4 border">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Weekly Attendance Rate</span>
                <Badge className={week_totals.rate >= 90 ? 'bg-green-100 text-green-800' : week_totals.rate >= 80 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}>
                  {week_totals.rate}%
                </Badge>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all duration-500 ${
                    week_totals.rate >= 90 ? 'bg-green-500' : 
                    week_totals.rate >= 80 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${week_totals.rate}%` }}
                />
              </div>
            </div>

            {/* Daily Breakdown */}
            <div className="bg-white rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-purple-50">
                    <TableHead className="font-semibold">Day</TableHead>
                    <TableHead className="text-center">Total</TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                        Present
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Clock className="h-3 w-3 text-amber-600" />
                        Late
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <XCircle className="h-3 w-3 text-red-600" />
                        Absent
                      </div>
                    </TableHead>
                    <TableHead className="text-center">Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {days.map((day) => {
                    const dayData = daily_summary[day] || { total: 0, present: 0, late: 0, absent: 0, rate: 0 };
                    const isToday = new Date().toLocaleDateString('en-US', { weekday: 'long' }) === day;
                    return (
                      <TableRow key={day} className={isToday ? 'bg-purple-50' : ''}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {day}
                            {isToday && <Badge variant="outline" className="text-xs">Today</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">{dayData.total}</TableCell>
                        <TableCell className="text-center">
                          <span className="text-green-600 font-medium">{dayData.present}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-amber-600 font-medium">{dayData.late}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-red-600 font-medium">{dayData.absent}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className={
                            dayData.rate >= 90 ? 'border-green-300 text-green-700' : 
                            dayData.rate >= 80 ? 'border-amber-300 text-amber-700' : 
                            dayData.total === 0 ? 'border-gray-300 text-gray-500' : 'border-red-300 text-red-700'
                          }>
                            {dayData.total > 0 ? `${dayData.rate}%` : '-'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        ) : (
          /* Detailed View - Student by Student */
          <div className="bg-white rounded-lg border overflow-hidden">
            {classWeeklyData ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-purple-50">
                    <TableHead className="font-semibold">#</TableHead>
                    <TableHead className="font-semibold">Student</TableHead>
                    {days.map(day => (
                      <TableHead key={day} className="text-center w-16">{getDayAbbrev(day)}</TableHead>
                    ))}
                    <TableHead className="text-center">Summary</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {classWeeklyData.students.map((student, idx) => (
                    <TableRow key={student.id}>
                      <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                      <TableCell className="font-medium">{student.name}</TableCell>
                      {days.map(day => (
                        <TableCell key={day} className="text-center">
                          {getStatusIcon(student.week[day])}
                        </TableCell>
                      ))}
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            {student.summary.present}
                          </Badge>
                          {student.summary.late > 0 && (
                            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                              {student.summary.late}
                            </Badge>
                          )}
                          {student.summary.absent > 0 && (
                            <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                              {student.summary.absent}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Select a class to view detailed weekly attendance</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
